import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { MapPin, BadgeCheck, Heart, Globe, MessageCircle, Instagram, Building2, User as UserIcon, Crown, Smartphone } from "lucide-react";
import { useApp } from "@/store";

const FLAG = { BR: "🇧🇷", AU: "🇦🇺", US: "🇺🇸" };

const linkHref = (kind, val) => {
  if (!val) return null;
  if (kind === "whatsapp") { if (val.startsWith("http")) return val; const d = val.replace(/[^0-9]/g, ""); return d ? `https://wa.me/${d}` : null; }
  if (kind === "instagram") { if (val.startsWith("http")) return val; return `https://instagram.com/${val.replace(/^@/, "")}`; }
  return val.startsWith("http") ? val : `https://${val}`;
};
const CARD_CONTACTS = [
  { key: "whatsapp", label: "WhatsApp", Icon: MessageCircle },
  { key: "website", label: "Website", Icon: Globe },
  { key: "instagram", label: "Instagram", Icon: Instagram },
];

export default function ListingCard({ item, index = 0, titleOverride }) {
  const { t, priceDisplay, formatMoney, favorites, toggleFavorite, user } = useApp();
  const navigate = useNavigate();
  const saved = favorites?.has(item.listing_id);
  const verified = item.owner_kyc_status === "approved";
  const isBusiness = item.owner_type === "business";
  const handleFav = (e) => { e.preventDefault(); e.stopPropagation(); if (!user) { navigate("/login"); return; } toggleFavorite(item.listing_id); };
  const links = item.contact_links || {};

  return (
    <Link to={`/listing/${item.listing_id}`} data-testid={`listing-card-${item.listing_id}`}
      className="group block overflow-hidden rounded-2xl border border-gray-200 bg-white transition-transform duration-300 hover:-translate-y-1 hover:shadow-lg rise"
      style={{ animationDelay: `${index * 60}ms` }}>
      <div className="relative aspect-[4/3] overflow-hidden bg-gray-100">
        <img src={item.image_url} alt={item.title}
          className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
        <span className="absolute left-3 top-3 rounded-full bg-white/90 px-2.5 py-1 text-xs font-bold text-gray-800 backdrop-blur">
          {FLAG[item.country]} {item.country}
        </span>
        <button data-testid={`fav-${item.listing_id}`} onClick={handleFav}
          className="absolute right-3 top-3 grid h-9 w-9 place-items-center rounded-full bg-black/40 backdrop-blur transition-transform hover:scale-110">
          <Heart size={17} className={saved ? "fill-[#FF3B30] text-[#FF3B30]" : "text-white"} />
        </button>
        {item.is_promoted && (
          <span data-testid="promoted-badge" className="absolute left-3 top-11 inline-flex items-center gap-1 rounded-full bg-[#FF3B30] px-2 py-0.5 text-[10px] font-bold text-white">★ {t("featured")}</span>
        )}
        {item.owner_is_pro && (
          <span data-testid={`pro-badge-${item.listing_id}`} className="absolute bottom-3 right-3 inline-flex items-center gap-1 rounded-full bg-[#0A0B10] px-2.5 py-1 text-[11px] font-bold text-yellow-300 shadow-md">
            <Crown size={13} /> {t("pro_business")}
          </span>
        )}
        {item.owner_phone_verified && (
          <span data-testid={`phone-verified-${item.listing_id}`} className={`absolute ${verified ? "bottom-11" : "bottom-3"} left-3 inline-flex items-center gap-1 rounded-full bg-white/95 px-2.5 py-1 text-[11px] font-bold text-[#155724] shadow-md`}>
            <Smartphone size={12} /> {t("phone_verified")}
          </span>
        )}
        {verified && (
          <span data-testid={`verified-${item.listing_id}`} className="absolute bottom-3 left-3 inline-flex items-center gap-1 rounded-full bg-[#002FA7] px-2.5 py-1 text-[11px] font-bold text-white shadow-md">
            <BadgeCheck size={13} /> {t("verified_owner")}
          </span>
        )}
      </div>
      <div className="p-5">
        <div className="mb-1 flex items-center justify-between">
          <span className="text-[11px] font-bold uppercase tracking-[0.15em] text-gray-400">{item.category}</span>
          <span data-testid={`adv-type-${item.listing_id}`} className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold ${isBusiness ? "bg-[#002FA7]/10 text-[#002FA7]" : "bg-gray-100 text-gray-500"}`}>
            {isBusiness ? <Building2 size={11} /> : <UserIcon size={11} />} {isBusiness ? t("person_business") : t("person_individual")}
          </span>
        </div>
        <h3 className="font-display text-lg font-bold leading-snug text-gray-900 line-clamp-1">{titleOverride || item.title}</h3>
        {item.owner_name && (
          <div className="mt-1 flex items-center gap-1 text-xs text-gray-500">
            by <span className="font-semibold text-gray-700">{item.owner_name}</span>
            {verified && <BadgeCheck size={13} className="text-[#002FA7]" />}
          </div>
        )}
        {item.city && (
          <div className="mt-1 flex items-center gap-1 text-sm text-gray-500"><MapPin size={13} /> {item.city}{item.distance_km != null && <span className="font-semibold text-[#002FA7]"> · {item.distance_km} km</span>}</div>
        )}
        <div className="mt-4 flex items-baseline gap-2">
          <span className="font-display text-xl font-extrabold text-[#002FA7]">{priceDisplay(item.price_per_day, item.currency)}</span>
          <span className="text-sm text-gray-400">{t("per_day")}</span>
        </div>
        <div className="mt-0.5 text-xs text-gray-400">{formatMoney(item.price_per_day, item.currency)} {item.currency}</div>
        {CARD_CONTACTS.some((c) => links[c.key]) && (
          <div className="mt-3 flex gap-2" data-testid={`card-contacts-${item.listing_id}`}>
            {CARD_CONTACTS.map(({ key, label, Icon }) => {
              const href = linkHref(key, links[key]);
              if (!href) return null;
              return (
                <button key={key} type="button" data-testid={`card-contact-${key}-${item.listing_id}`} title={label}
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); window.open(href, "_blank", "noopener,noreferrer"); }}
                  className="grid h-8 w-8 place-items-center rounded-full border border-gray-200 text-gray-500 transition-colors hover:border-[#002FA7] hover:text-[#002FA7]">
                  <Icon size={15} />
                </button>
              );
            })}
          </div>
        )}
      </div>
    </Link>
  );
}
